import type { BuildState, GearSlot, SlotKey } from './build-state.svelte.js';
import type { GameData } from './data.js';
import { computeBuild, SLOT_KEYS } from './build-state.svelte.js';

export interface ContributionRow {
  bucket: 'gear' | 'sets' | 'brands' | 'spec' | 'weapon_talent';
  labelKey: string;
  contributionPct: number;
}

function emptySlot(): GearSlot {
  return {
    brandId: null,
    setId: null,
    namedId: null,
    coreStat: 'wd',
    attr1: null,
    attr2: null,
    modAttr: null,
    isPrototype: false,
    prototypeAugmentId: null,
  };
}

function snapshotGear(state: BuildState): Record<SlotKey, GearSlot> {
  const out = {} as Record<SlotKey, GearSlot>;
  for (const k of SLOT_KEYS) {
    const s = state.gear[k];
    out[k] = {
      brandId: s.brandId ?? null,
      setId: s.setId ?? null,
      namedId: s.namedId ?? null,
      coreStat: s.coreStat,
      attr1: s.attr1 ?? null,
      attr2: s.attr2 ?? null,
      modAttr: s.modAttr ?? null,
      isPrototype: s.isPrototype ?? false,
      prototypeAugmentId: s.prototypeAugmentId ?? null,
    };
  }
  return out;
}

/** Wrap state with a Proxy that overrides selected fields without mutating the original. */
function withOverrides(state: BuildState, overrides: Record<string, unknown>): BuildState {
  return new Proxy(state as unknown as Record<string, unknown>, {
    get(target, prop: string) {
      if (prop in overrides) return overrides[prop];
      return target[prop];
    },
  }) as unknown as BuildState;
}

/** Compute % contribution of 5 source buckets to current burst DPS.
 *  contribution = (full_dps - dps_without_bucket) / full_dps × 100
 */
export function computeContributions(state: BuildState, data: GameData): ContributionRow[] {
  const baseline = computeBuild(state, data).dps.burstDps;
  if (!baseline || baseline <= 0) return [];

  const baseGear = snapshotGear(state);

  // Bucket 1: All gear (clear all 6 slots)
  const noGear: Record<SlotKey, GearSlot> = {
    chest: emptySlot(), backpack: emptySlot(), gloves: emptySlot(),
    mask: emptySlot(), holster: emptySlot(), kneepads: emptySlot(),
  };

  // Bucket 2: Sets only (clear setIds + talents, keep brands/named/cores/attrs/mods)
  const noSets: Record<SlotKey, GearSlot> = {} as Record<SlotKey, GearSlot>;
  for (const k of SLOT_KEYS) {
    noSets[k] = { ...baseGear[k], setId: null };
  }

  // Bucket 3: Brands only (clear brandIds, keep sets/named)
  const noBrands: Record<SlotKey, GearSlot> = {} as Record<SlotKey, GearSlot>;
  for (const k of SLOT_KEYS) {
    noBrands[k] = { ...baseGear[k], brandId: null };
  }

  const rows: ContributionRow[] = [];

  // 1. Gear (all slots cleared)
  const dpsNoGear = computeBuild(withOverrides(state, { gear: noGear }), data).dps.burstDps;
  rows.push({
    bucket: 'gear',
    labelKey: 'contrib_gear',
    contributionPct: ((baseline - dpsNoGear) / baseline) * 100,
  });

  // 2. Sets (4pc + chest/bp talents)
  const dpsNoSets = computeBuild(withOverrides(state, {
    gear: noSets,
    chestTalentActive: false,
    backpackTalentActive: false,
    setStacks: {},
    setChestTalent: {},
    setBpTalent: {},
  }), data).dps.burstDps;
  rows.push({
    bucket: 'sets',
    labelKey: 'contrib_sets',
    contributionPct: ((baseline - dpsNoSets) / baseline) * 100,
  });

  // 3. Brands (2pc/3pc bonuses)
  const dpsNoBrands = computeBuild(withOverrides(state, { gear: noBrands }), data).dps.burstDps;
  rows.push({
    bucket: 'brands',
    labelKey: 'contrib_brands',
    contributionPct: ((baseline - dpsNoBrands) / baseline) * 100,
  });

  // 4. Spec + SCP class picks
  const dpsNoSpec = computeBuild(withOverrides(state, {
    specializationId: null,
    specClassPicks: [] as string[],
    specMmrRifleHsd: false,
    activeSpecPerks: [] as string[],
  }), data).dps.burstDps;
  rows.push({
    bucket: 'spec',
    labelKey: 'contrib_spec',
    contributionPct: ((baseline - dpsNoSpec) / baseline) * 100,
  });

  // 5. Weapon talent
  const dpsNoWpnTal = computeBuild(withOverrides(state, {
    weaponTalentActive: false,
    weaponTalentStacks: 0,
  }), data).dps.burstDps;
  rows.push({
    bucket: 'weapon_talent',
    labelKey: 'contrib_weapon_talent',
    contributionPct: ((baseline - dpsNoWpnTal) / baseline) * 100,
  });

  return rows.sort((a, b) => b.contributionPct - a.contributionPct);
}

export const CONTRIB_LABELS: Record<string, { en: string; ru: string }> = {
  contrib_gear: { en: 'All gear (6 slots)', ru: 'Вся экипировка (6 слотов)' },
  contrib_sets: { en: 'Set 4pc + chest/backpack talents', ru: 'Сет 4pc + таланты нагрудника/рюкзака' },
  contrib_brands: { en: 'Brand bonuses (2pc/3pc)', ru: 'Бонусы брендов (2pc/3pc)' },
  contrib_spec: { en: 'Specialization + SCP class picks', ru: 'Специализация + классовые пики' },
  contrib_weapon_talent: { en: 'Weapon talent', ru: 'Талант оружия' },
};
